import type { OgAnalytics, ResolvedMode } from '@opengov/analytics';
import { createContext, useContext } from 'react';

/**
 * The analytics surface the rest of reporting-app codes against.
 *
 * Deliberately free of any runtime import from `@opengov/analytics`: the import above is type-only
 * and erased at build time. The package itself is loaded by `OgAnalyticsGate`, and only in
 * integration and local development builds with `product-reporting-feature-enable-posthog` on.
 * Every other build never bundles it, so code outside `ogAnalyticsRuntime.tsx` must not import it
 * at runtime.
 *
 * `mode` is `'disabled'` on the no-op client, so `mode === 'standalone'` checks also exclude it.
 */
export type ReportingOgAnalytics = Pick<OgAnalytics, 'track' | 'page' | 'identify' | 'group' | 'reset'> & {
  readonly mode: ResolvedMode | 'disabled';
};

export const noopOgAnalytics: ReportingOgAnalytics = {
  mode: 'disabled',
  track: () => undefined,
  page: () => undefined,
  identify: () => undefined,
  group: () => undefined,
  reset: () => Promise.resolve()
};

export const OgAnalyticsContext = createContext<ReportingOgAnalytics>(noopOgAnalytics);

export const useOgAnalytics = (): ReportingOgAnalytics => useContext(OgAnalyticsContext);

/** `source_app` on reporting-app's events, telling them apart from other clients reporting the same product. */
export const OG_ANALYTICS_SOURCE_APP = 'reporting-app';
